/**
 * Enhanced Flow Builder Panel
 * Visual flow builder with create dialog (Trigger → Condition → Action)
 */

import { useState } from "react";
import { usePipelineIntelligence } from "@/data/pipelineIntelligence/context";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Zap, Plus, Eye, Trash2, Phone, MessageSquare, Mail, Brain,
  ChevronDown, ChevronUp, ArrowDown, GripVertical,
} from "lucide-react";
import type {
  ResponseFlow, FlowStep, FlowTriggerCondition,
  SequenceChannel, AIMode, AIToneProfile,
} from "@/data/pipelineIntelligence/types";

const CHANNEL_ICONS: Record<SequenceChannel, React.ElementType> = {
  sms: MessageSquare, email: Mail, call: Phone, ai_message: Brain,
};

const CHANNEL_LABELS: Record<SequenceChannel, string> = {
  sms: "SMS", email: "Email", call: "Call", ai_message: "AI Message",
};

function formatMinutes(mins: number): string {
  if (mins === 0) return "Instant";
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
}

export function FlowBuilderPanel() {
  const { flows, toggleFlow, deleteFlow, sourceProfiles, templates } = usePipelineIntelligence();
  const [selectedFlow, setSelectedFlow] = useState<ResponseFlow | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Response Flows</h2>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Define what actions are executed: step-by-step communication, channels, and delays
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[10px]">
            {flows.filter(f => f.enabled).length}/{flows.length} active
          </Badge>
          <Button size="sm" className="h-7 text-xs gap-1" onClick={() => setShowCreate(true)}>
            <Plus className="h-3 w-3" /> New Flow
          </Button>
        </div>
      </div>

      <div className="grid gap-3">
        {flows.map(flow => {
          const source = sourceProfiles.find(s => s.id === flow.sourceId);
          return (
            <Card key={flow.id} className={cn("shadow-none transition-colors", !flow.enabled && "opacity-60")}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Switch checked={flow.enabled} onCheckedChange={() => toggleFlow(flow.id)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">{flow.name}</p>
                      <Badge variant="outline" className="text-[9px] h-4">v{flow.version}</Badge>
                      <Badge variant="outline" className="text-[9px] h-4">P{flow.priority}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{flow.description}</p>
                    {source && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Inherits strategy from: <span className="font-medium text-foreground">{source.sourceName}</span>
                        {" "}({source.responseUrgency} urgency, {source.aiTone} tone)
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="secondary" className="text-[9px]">
                      {source?.sourceName || (flow.sourceId === "all" ? "All Sources" : flow.sourceId)}
                    </Badge>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setSelectedFlow(flow); setShowDetail(true); }}>
                      <Eye className="h-3 w-3 mr-1" /> View
                    </Button>
                  </div>
                </div>

                {/* Visual Step Chain */}
                <div className="flex items-center gap-1 mt-3 overflow-x-auto">
                  {flow.steps.map((step, i) => {
                    const Icon = CHANNEL_ICONS[step.channel];
                    return (
                      <div key={step.id} className="flex items-center gap-1">
                        {i > 0 && (
                          <div className="flex items-center">
                            <div className="w-4 border-t border-dashed border-border" />
                            <span className="text-[9px] text-muted-foreground px-0.5">{formatMinutes(step.delayMinutes)}</span>
                            <div className="w-4 border-t border-dashed border-border" />
                          </div>
                        )}
                        <div className={cn(
                          "flex items-center gap-1 px-2 py-1 rounded-md border text-[10px]",
                          step.channel === "ai_message" ? "bg-primary/5 border-primary/20 text-primary" : "bg-muted/40 border-border/60"
                        )}>
                          <Icon className="h-3 w-3" />
                          <span className="whitespace-nowrap">{step.label}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Flow Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedFlow && (
            <FlowDetailView flow={selectedFlow} />
          )}
        </DialogContent>
      </Dialog>

      {/* Create Flow Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <CreateFlowDialog onClose={() => setShowCreate(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FlowDetailView({ flow }: { flow: ResponseFlow }) {
  const { sourceProfiles, templates } = usePipelineIntelligence();
  const MODE_LABELS: Record<AIMode, string> = {
    disabled: "Disabled", suggest_only: "Suggest Only",
    auto_with_approval: "Auto + Approval", auto_send: "Auto Send",
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{flow.name}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-muted-foreground">Source:</span> {sourceProfiles.find(s => s.id === flow.sourceId)?.sourceName || "All"}</div>
          <div><span className="text-muted-foreground">Funnel:</span> {flow.funnel}</div>
          <div><span className="text-muted-foreground">Priority:</span> {flow.priority}</div>
          <div><span className="text-muted-foreground">Version:</span> {flow.version}</div>
        </div>

        <Separator />

        <div>
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Trigger Conditions</h4>
          <div className="space-y-1">
            {flow.triggerConditions.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="text-[9px]">{c.field}</Badge>
                <span className="text-muted-foreground">{c.operator}</span>
                <Badge variant="secondary" className="text-[9px]">{c.value}</Badge>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        <div>
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Sequence Steps</h4>
          <div className="space-y-2">
            {flow.steps.map((step, i) => {
              const Icon = CHANNEL_ICONS[step.channel];
              const tpl = templates.find(t => t.id === step.templateId);
              return (
                <div key={step.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm font-medium">{step.label}</span>
                      {i > 0 && <span className="text-[10px] text-muted-foreground">after {formatMinutes(step.delayMinutes)}</span>}
                      {step.aiMode !== "disabled" && <Badge className="text-[9px] h-4 bg-primary/10 text-primary border-primary/20">{MODE_LABELS[step.aiMode]}</Badge>}
                    </div>
                    {tpl && <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{tpl.body}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      {step.stopOnReply && <Badge variant="outline" className="text-[9px] h-4">Stop on reply</Badge>}
                      {step.stopOnBooking && <Badge variant="outline" className="text-[9px] h-4">Stop on booking</Badge>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

function CreateFlowDialog({ onClose }: { onClose: () => void }) {
  const { addFlow, sourceProfiles, templates } = usePipelineIntelligence();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sourceId, setSourceId] = useState("all");
  const [funnel, setFunnel] = useState("high_intent");
  const [priority, setPriority] = useState(50);
  const [conditions, setConditions] = useState<FlowTriggerCondition[]>([
    { field: "source", operator: "equals", value: "" },
  ]);
  const [steps, setSteps] = useState<Omit<FlowStep, "id">[]>([
    { order: 0, delayMinutes: 0, channel: "sms", templateId: "", aiMode: "disabled", aiTone: "balanced", stopOnReply: true, stopOnBooking: true, label: "Initial SMS" },
  ]);

  const addStep = () => {
    setSteps(prev => [...prev, {
      order: prev.length,
      delayMinutes: 60,
      channel: "sms",
      templateId: "",
      aiMode: "disabled",
      aiTone: "balanced",
      stopOnReply: true,
      stopOnBooking: true,
      label: `Step ${prev.length + 1}`,
    }]);
  };

  const updateStep = (idx: number, updates: Partial<Omit<FlowStep, "id">>) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, ...updates } : s));
  };

  const removeStep = (idx: number) => {
    setSteps(prev => prev.filter((_, i) => i !== idx));
  };

  const addCondition = () => {
    setConditions(prev => [...prev, { field: "source", operator: "equals", value: "" }]);
  };

  const updateCondition = (idx: number, updates: Partial<FlowTriggerCondition>) => {
    setConditions(prev => prev.map((c, i) => i === idx ? { ...c, ...updates } : c));
  };

  const removeCondition = (idx: number) => {
    setConditions(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    if (!name.trim()) { toast.error("Flow name is required"); return; }
    if (steps.length === 0) { toast.error("Add at least one step"); return; }

    const flow: ResponseFlow = {
      id: `flow-${Date.now()}`,
      name,
      description,
      sourceId,
      funnel: funnel as any,
      triggerConditions: conditions.filter(c => c.value),
      steps: steps.map((s, i) => ({ ...s, id: `fs-${Date.now()}-${i}`, order: i })),
      enabled: true,
      version: 1,
      priority,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: "Operator",
    };

    addFlow(flow);
    toast.success("Response flow created");
    onClose();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Zap className="h-4 w-4" /> Create Response Flow
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-5">
        {/* Basic Info */}
        <section className="space-y-3">
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Flow Details</h4>
          <Input placeholder="Flow name" value={name} onChange={e => setName(e.target.value)} className="h-8 text-xs" />
          <Textarea placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} className="text-xs min-h-[50px]" />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Source</label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {sourceProfiles.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.sourceName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Funnel</label>
              <Select value={funnel} onValueChange={setFunnel}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high_intent">High Intent</SelectItem>
                  <SelectItem value="retargeting">Retargeting</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="winback">Winback</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Priority</label>
              <Input type="number" value={priority} onChange={e => setPriority(parseInt(e.target.value) || 50)} className="h-8 text-xs" min={1} max={100} />
            </div>
          </div>
        </section>

        <Separator />

        {/* Trigger Conditions */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Trigger Conditions</h4>
            <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={addCondition}>
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
          <div className="space-y-2">
            {conditions.map((cond, i) => (
              <div key={i} className="flex items-center gap-2">
                <Select value={cond.field} onValueChange={(v: any) => updateCondition(i, { field: v })}>
                  <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="source">Source</SelectItem>
                    <SelectItem value="funnel">Funnel</SelectItem>
                    <SelectItem value="stage">Stage</SelectItem>
                    <SelectItem value="lead_score">Lead Score</SelectItem>
                    <SelectItem value="no_reply_minutes">No Reply (min)</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={cond.operator} onValueChange={(v: any) => updateCondition(i, { operator: v })}>
                  <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equals">Equals</SelectItem>
                    <SelectItem value="not_equals">Not Equals</SelectItem>
                    <SelectItem value="greater_than">Greater Than</SelectItem>
                    <SelectItem value="less_than">Less Than</SelectItem>
                    <SelectItem value="contains">Contains</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Value"
                  value={cond.value}
                  onChange={e => updateCondition(i, { value: e.target.value })}
                  className="h-7 text-xs flex-1"
                />
                {conditions.length > 1 && (
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground" onClick={() => removeCondition(i)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </section>

        <Separator />

        {/* Sequence Steps */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Sequence Steps</h4>
            <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={addStep}>
              <Plus className="h-3 w-3" /> Add Step
            </Button>
          </div>
          <div className="space-y-2">
            {steps.map((step, i) => {
              const Icon = CHANNEL_ICONS[step.channel];
              return (
                <div key={i} className="p-3 rounded-lg bg-muted/20 border border-border/40 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold shrink-0">
                      {i + 1}
                    </div>
                    {i > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] text-muted-foreground">Wait</span>
                        <Input
                          type="number" min={0}
                          value={step.delayMinutes}
                          onChange={e => updateStep(i, { delayMinutes: parseInt(e.target.value) || 0 })}
                          className="h-6 text-[10px] w-16"
                        />
                        <span className="text-[9px] text-muted-foreground">min</span>
                      </div>
                    )}
                    <div className="flex-1" />
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground" onClick={() => removeStep(i)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[9px] text-muted-foreground">Channel</label>
                      <Select value={step.channel} onValueChange={(v: any) => updateStep(i, { channel: v })}>
                        <SelectTrigger className="h-7 text-xs mt-0.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(Object.entries(CHANNEL_LABELS) as [SequenceChannel, string][]).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[9px] text-muted-foreground">Label</label>
                      <Input
                        value={step.label}
                        onChange={e => updateStep(i, { label: e.target.value })}
                        className="h-7 text-xs mt-0.5"
                        placeholder="Step label"
                      />
                    </div>
                    <div>
                      <label className="text-[9px] text-muted-foreground">AI Mode</label>
                      <Select value={step.aiMode} onValueChange={(v: any) => updateStep(i, { aiMode: v })}>
                        <SelectTrigger className="h-7 text-xs mt-0.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="disabled">Disabled</SelectItem>
                          <SelectItem value="suggest_only">Suggest Only</SelectItem>
                          <SelectItem value="auto_with_approval">Auto + Approval</SelectItem>
                          <SelectItem value="auto_send">Auto Send</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <Switch checked={step.stopOnReply} onCheckedChange={v => updateStep(i, { stopOnReply: v })} className="scale-75" />
                      Stop on reply
                    </label>
                    <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <Switch checked={step.stopOnBooking} onCheckedChange={v => updateStep(i, { stopOnBooking: v })} className="scale-75" />
                      Stop on booking
                    </label>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Visual Preview */}
          {steps.length > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-muted/10 border border-border/20">
              <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Flow Preview</p>
              <div className="flex items-center gap-1 overflow-x-auto">
                {steps.map((step, i) => {
                  const Icon = CHANNEL_ICONS[step.channel];
                  return (
                    <div key={i} className="flex items-center gap-1">
                      {i > 0 && (
                        <div className="flex items-center">
                          <div className="w-3 border-t border-dashed border-border" />
                          <span className="text-[8px] text-muted-foreground px-0.5">{formatMinutes(step.delayMinutes)}</span>
                          <div className="w-3 border-t border-dashed border-border" />
                        </div>
                      )}
                      <div className={cn(
                        "flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px]",
                        step.channel === "ai_message" ? "bg-primary/5 border-primary/20 text-primary" : "bg-muted/40 border-border/60"
                      )}>
                        <Icon className="h-2.5 w-2.5" />
                        <span className="whitespace-nowrap">{step.label || CHANNEL_LABELS[step.channel]}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose} className="text-xs">Cancel</Button>
        <Button onClick={handleSave} className="text-xs gap-1">
          <Zap className="h-3 w-3" /> Create Flow
        </Button>
      </DialogFooter>
    </>
  );
}
